import { c as _c } from "react/compiler-runtime";
import { useLiveQuery } from 'dexie-react-hooks';
import db, { FeedDonation } from 'indexed-db';
import Dexie from 'dexie';
import { getErrorMessage } from 'indexed-db/errors';
import { useCallback, useEffect, useEffectEvent, useState } from 'react';
import type { FEPPContentDonation } from '../../types';
import useHandleDbError from '../useHandleDbError';
export function useHandleDBError() {
  const $ = _c(2);
  const handleError = useHandleDbError();
  let t0;
  if ($[0] !== handleError) {
    t0 = error => {
      const message = getErrorMessage(error);
      handleError({
        action: "error",
        message
      });
    };
    $[0] = handleError;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
export function useGetLoadedDonationCount(fundraisingEventId) {
  const $ = _c(5);
  const handleError = useHandleDBError();
  let t0;
  if ($[0] !== fundraisingEventId || $[1] !== handleError) {
    t0 = async () => {
      const count = await db.fundraisingEventDonationFeed.where("fundraisingEventId").equals(fundraisingEventId).count().catch(handleError);
      return count ?? null;
    };
    $[0] = fundraisingEventId;
    $[1] = handleError;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== fundraisingEventId) {
    t1 = [fundraisingEventId];
    $[3] = fundraisingEventId;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return useLiveQuery(t0, t1);
}
export function useGetDonationsFromDB(fundraisingEventId, limit, offset, handleError) {
  const $ = _c(7);
  let t0;
  if ($[0] !== fundraisingEventId || $[1] !== handleError || $[2] !== limit || $[3] !== offset) {
    t0 = async () => {
      ;
      try {
        return db.transaction("r", [db.fundraisingEventDonationFeed, db.fundraisingEventDonationFeedMetaData], async trans => {
          const getLastSeenDonationPosition = async function getLastSeenDonationPosition() {
            const lastSeenDonationId = (await trans.fundraisingEventDonationFeedMetaData.get(fundraisingEventId))?.lastSeenDonationId;
            if (lastSeenDonationId === undefined) {
              return 0;
            }
            return await trans.fundraisingEventDonationFeed.where("[fundraisingEventId+confirmedAt]").between([fundraisingEventId, Dexie.minKey], [fundraisingEventId, Dexie.maxKey]).reverse().until(donation => donation.id === lastSeenDonationId, false).count();
          };
          return await Promise.all([db.fundraisingEventDonationFeed.where("[fundraisingEventId+confirmedAt]").between([fundraisingEventId, Dexie.minKey], [fundraisingEventId, Dexie.maxKey]).reverse().offset(offset).limit(limit).toArray(), getLastSeenDonationPosition()]);
        });
      } catch (t1) {
        const error = t1;
        handleError(error);
        return [[], 0];
      }
    };
    $[0] = fundraisingEventId;
    $[1] = handleError;
    $[2] = limit;
    $[3] = offset;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  const getDonations = t0;
  let t1;
  if ($[5] !== getDonations) {
    t1 = {
      getDonations
    };
    $[5] = getDonations;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
}
export function useGetDonations(fundraisingEventId, limit, offset, currentRow) {
  const $ = _c(19);
  const handleError = useHandleDBError();
  const {
    getDonations
  } = useGetDonationsFromDB(fundraisingEventId, limit, offset, handleError);
  let t0;
  if ($[0] !== getDonations || $[1] !== handleError) {
    t0 = [getDonations, handleError];
    $[0] = getDonations;
    $[1] = handleError;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [[], 0];
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const [donations, lastSeenDonationPosition] = useLiveQuery(getDonations, t0, t1 as [FeedDonation[], number]);
  let t2;
  if ($[4] !== currentRow || $[5] !== donations || $[6] !== lastSeenDonationPosition || $[7] !== offset) {
    t2 = {
      offset,
      row: currentRow,
      data: donations,
      lastSeenDonationPosition
    };
    $[4] = currentRow;
    $[5] = donations;
    $[6] = lastSeenDonationPosition;
    $[7] = offset;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const [loadedState, setLoadedState] = useState(t2);
  let t3;
  if ($[9] !== currentRow || $[10] !== offset) {
    t3 = (donations_0, lastSeenDonationPosition_0) => setLoadedState({
      offset,
      row: currentRow,
      data: donations_0,
      lastSeenDonationPosition: lastSeenDonationPosition_0
    });
    $[9] = currentRow;
    $[10] = offset;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const setNewState = useEffectEvent(t3);
  let t4;
  if ($[12] !== donations || $[13] !== lastSeenDonationPosition || $[14] !== setNewState) {
    t4 = () => {
      if (!donations) {
        return;
      }
      setNewState(donations, lastSeenDonationPosition);
    };
    $[12] = donations;
    $[13] = lastSeenDonationPosition;
    $[14] = setNewState;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== donations || $[17] !== lastSeenDonationPosition) {
    t5 = [donations, lastSeenDonationPosition];
    $[16] = donations;
    $[17] = lastSeenDonationPosition;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  useEffect(t4, t5);
  return loadedState;
}
export function useGetTotalDonationAmount(fundraisingEventId) {
  const handleError = useHandleDBError();
  return useLiveQuery(async () => {
    const metaData = await db.fundraisingEventDonationFeedMetaData.get(fundraisingEventId).catch(handleError);
    return metaData?.receivedAmountInCents ?? null;
  }, [fundraisingEventId]) ?? null;
}
export function useGetTotalDonationCount(fundraisingEventId) {
  const handleError = useHandleDBError();
  return useLiveQuery(async () => {
    const metaData = await db.fundraisingEventDonationFeedMetaData.get(fundraisingEventId).catch(handleError);
    return metaData?.receivedDonationsCount ?? null;
  }, [fundraisingEventId]) ?? null;
}
export function useSetAllNewDonationsAsSeen(fundraisingEventId) {
  const $ = _c(3);
  const handleError = useHandleDBError();
  let t0;
  if ($[0] !== fundraisingEventId || $[1] !== handleError) {
    t0 = () => {
      db.transaction("rw", [db.fundraisingEventDonationFeed, db.fundraisingEventDonationFeedMetaData], async trans => {
        const newestDonationId = (await trans.fundraisingEventDonationFeed.where("[fundraisingEventId+confirmedAt]").between([fundraisingEventId, Dexie.minKey], [fundraisingEventId, Dexie.maxKey]).reverse().first())?.id;
        return await Promise.all([trans.fundraisingEventDonationFeed.where("fundraisingEventId").equals(fundraisingEventId).modify({
          markedAsSeen: true
        }), trans.fundraisingEventDonationFeedMetaData.update(fundraisingEventId, {
          lastSeenAt: new Date().toISOString(),
          lastSeenDonationId: newestDonationId ?? undefined
        })]);
      }).then().catch(handleError);
    };
    $[0] = fundraisingEventId;
    $[1] = handleError;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const callback = t0;
  return callback;
}
export function useInsertInitialDonations(fundraisingEventId, initialDonations, initialDonationCount, initialDonationAmountInCents) {
  const $ = _c(9);
  const handleError = useHandleDBError();
  let t0;
  if ($[0] !== fundraisingEventId || $[1] !== handleError) {
    t0 = async (donations, receivedDonationsCount, receivedAmountInCents) => {
      ;
      try {
        await db.transaction("rw", [db.fundraisingEventDonationFeed, db.fundraisingEventDonationFeedMetaData], async trans => {
          const now = new Date();
          const nowStr = now.toISOString();
          let donationsToSave = [];
          const ids = [];
          for (const donation of donations) {
            if (!donation) {
              continue;
            }
            if (!donation.confirmedAt) {
              continue;
            }
            const markedAsSeen = now.getTime() - new Date(donation.confirmedAt).getTime() > 60000;
            ids.push(donation.id);
            donationsToSave.push({
              ...donation,
              fundraisingEventId,
              markedAsSeen,
              syncedAt: nowStr,
              createdAt: new Date(donation.createdAt).toISOString(),
              confirmedAt: new Date(donation.confirmedAt).toISOString()
            });
          }
          const existingKeys = new Set(await db.fundraisingEventDonationFeed.where("id").anyOf(ids).primaryKeys());
          donationsToSave = donationsToSave.filter(d => !existingKeys.has(d.id));
          return await Promise.all([trans.fundraisingEventDonationFeed.bulkPut(donationsToSave), trans.fundraisingEventDonationFeedMetaData.update(fundraisingEventId, {
            receivedDonationsCount,
            receivedAmountInCents
          })]);
        });
      } catch (t1) {
        const error = t1;
        handleError(error);
      }
    };
    $[0] = fundraisingEventId;
    $[1] = handleError;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const insertInitialDonations = t0;
  let t1;
  let t2;
  if ($[3] !== initialDonationAmountInCents || $[4] !== initialDonationCount || $[5] !== initialDonations || $[6] !== insertInitialDonations) {
    t1 = () => {
      if (!initialDonations.length) {
        return;
      }
      insertInitialDonations(initialDonations, initialDonationCount, initialDonationAmountInCents);
    };
    t2 = [initialDonations, initialDonationCount, initialDonationAmountInCents, insertInitialDonations];
    $[3] = initialDonationAmountInCents;
    $[4] = initialDonationCount;
    $[5] = initialDonations;
    $[6] = insertInitialDonations;
    $[7] = t1;
    $[8] = t2;
  } else {
    t1 = $[7];
    t2 = $[8];
  }
  useEffect(t1, t2);
  return insertInitialDonations;
}